/**
 * FactorContributionChart.tsx
 * 
 * Epic 54.2: Horizontal bar chart showing each factor's contribution to VeraScore.
 * Uses Recharts for native React integration with Tailwind styling.
 * 
 * Displays: 6 factors (DTI, Cash on Hand, Spending, Savings, Debt, Payment History)
 * Each bar shows the factor score (0-100) with color coding by health status.
 */

import { FC, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';
import type { FinancialHealthStats } from '../../types/financialStats';

interface FactorContributionChartProps {
  /** Latest month's financial stats */
  stats: FinancialHealthStats;
  /** Optional: Show debug information */
  showDebug?: boolean;
  /** Optional: Click handler for drilling into factor details */
  onFactorClick?: (factorKey: string) => void;
}

// Factor configuration with labels and weights
// BUG-UI-040 FIX: Renamed "Cash on Hand" to "Liquid Reserves" for consistency
const FACTOR_CONFIG = [
  { key: 'factor_dti', label: 'Debt to Income', weight: 0.25, shortLabel: 'DTI' },
  { key: 'factor_cash_on_hand', label: 'Liquid Reserves', weight: 0.20, shortLabel: 'Reserves' },
  { key: 'factor_spending', label: 'Spending', weight: 0.15, shortLabel: 'Spend' },
  { key: 'factor_savings', label: 'Savings', weight: 0.15, shortLabel: 'Save' },
  { key: 'factor_debt', label: 'Debt Management', weight: 0.15, shortLabel: 'Debt' },
  { key: 'factor_payment_history', label: 'Payment History', weight: 0.10, shortLabel: 'Pmts' },
] as const;

// Health status colors (from tailwind.config.js)
const getHealthColor = (score: number): string => {
  if (score >= 80) return '#2AD352'; // status.healthy (Excellent)
  if (score >= 60) return '#2196F3'; // status.fair (Good)
  if (score >= 40) return '#FF800C'; // status.needsImprovement (Fair)
  return '#FF6174'; // status.atRisk (Poor)
};

// Custom tooltip component
const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  
  const data = payload[0].payload;
  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-slate-200 p-3 min-w-[180px]">
      <p className="font-semibold text-slate-800 mb-1">{data.label}</p>
      <div className="flex justify-between items-center">
        <span className="text-slate-600 text-sm">Score:</span>
        <span className="font-bold text-lg" style={{ color: getHealthColor(data.score) }}>
          {data.score?.toFixed(0) ?? '—'}
        </span>
      </div>
      <div className="flex justify-between items-center mt-1">
        <span className="text-slate-500 text-xs">Weight:</span>
        <span className="text-slate-600 text-sm">{(data.weight * 100).toFixed(0)}%</span>
      </div>
      <div className="flex justify-between items-center mt-1">
        <span className="text-slate-500 text-xs">Contribution:</span>
        <span className="text-slate-600 text-sm font-medium">
          {((data.score ?? 0) * data.weight).toFixed(1)} pts
        </span>
      </div>
    </div>
  );
};

export const FactorContributionChart: FC<FactorContributionChartProps> = ({
  stats,
  showDebug = false,
  onFactorClick,
}) => {
  // Transform stats into chart data
  const chartData = useMemo(() => {
    return FACTOR_CONFIG.map(factor => {
      const score = stats[factor.key as keyof FinancialHealthStats] as number | null;
      return {
        key: factor.key,
        label: factor.label,
        shortLabel: factor.shortLabel,
        score: score ?? 0,
        weight: factor.weight,
        contribution: (score ?? 0) * factor.weight,
      };
    }).sort((a, b) => b.score - a.score); // Sort by score descending
  }, [stats]);

  // Calculate total weighted score
  const totalScore = useMemo(() => {
    return chartData.reduce((sum, item) => sum + item.contribution, 0);
  }, [chartData]);

  // Custom Y-axis tick that's clickable - BUG-UI-013 fix
  // BUG-UI-025: Use CSS hover instead of React state for better performance
  const CustomYAxisTick = ({ x, y, payload }: any) => {
    const item = chartData.find(d => d.label === payload.value);
    const isClickable = !!onFactorClick;
    return (
      <g
        transform={`translate(${x},${y})`}
        onClick={() => item && onFactorClick?.(item.key)}
        style={{ cursor: isClickable ? 'pointer' : 'default' }}
        className={isClickable ? 'factor-tick-clickable' : ''}
      >
        <text
          x={-5}
          y={0}
          dy={4}
          textAnchor="end"
          fill="#334155"
          fontSize={12}
          fontWeight={500}
          className={isClickable ? 'factor-tick-text' : ''}
        >
          {payload.value}
        </text>
      </g>
    );
  };

  return (
    <div className="w-full factor-contribution-chart">
      {/* CSS for SVG hover effects */}
      <style>{`
        .factor-contribution-chart .factor-tick-clickable:hover text {
          fill: #3B82F6 !important;
          font-weight: 600 !important;
        }
      `}</style>
      {showDebug && (
        <div className="text-xs text-slate-500 mb-2">
          Total weighted score: {totalScore.toFixed(1)}
        </div>
      )}

      <ResponsiveContainer width="100%" height={280}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
          barCategoryGap="20%"
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#E2E8F0" />
          <XAxis
            type="number"
            domain={[0, 100]}
            tick={{ fill: '#64748B', fontSize: 11 }}
            tickLine={false}
            axisLine={{ stroke: '#E2E8F0' }}
          />
          <YAxis
            type="category"
            dataKey="label"
            tick={<CustomYAxisTick />}
            tickLine={false}
            axisLine={false}
            width={75}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }} />
          <Bar
            dataKey="score"
            radius={[0, 4, 4, 0]}
            onClick={(data) => onFactorClick?.(data.key)}
            className="cursor-pointer"
            minPointSize={8}  // Increased for better clickability on 0-score bars
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getHealthColor(entry.score)} />
            ))}
            <LabelList
              dataKey="score"
              position="right"
              formatter={(value: number) => value?.toFixed(0)}
              style={{ fill: '#64748B', fontSize: 11, fontWeight: 600 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default FactorContributionChart;

